import type { AlgoliaSearchOptions } from "algoliasearch/dist/algoliasearch-lite"
import type { SearchClient } from "algoliasearch/lite"

import { AuthMode } from "@algolia/client-common"

import { hideAndReplaceString } from "@/modules/format/string/hideAndReplaceString"
import { logger } from "@/modules/monitoring/logger"
import { getSearchProxyHosts } from "@/modules/search/getSearchProxyHosts"

export type SearchEngineOptions = {
  userToken: string | null
  isDirectAlgoliaConnectionEnabled: boolean
  isSearchProxyV3ConnectionEnabled: boolean
  isMultiHostConnectionEnabled: boolean
}

const getSearchEngine = async (options?: SearchEngineOptions): Promise<SearchClient> => {
  const { default: algoliasearch } = await import("algoliasearch/lite")

  const userToken = options?.userToken
  const canUseDirectAlgoliaConnection = options?.isDirectAlgoliaConnectionEnabled && typeof window === "undefined"

  const algoliaApiKey = canUseDirectAlgoliaConnection
    ? `${process.env.ALGOLIA_SERVER_API_KEY || ""}`
    : process.env.NEXT_PUBLIC_ALGOLIA_API_KEY

  const searchProxyHosts = getSearchProxyHosts({
    isDirectAlgoliaConnectionEnabled: options?.isDirectAlgoliaConnectionEnabled,
    isMultiHostConnectionEnabled: options?.isMultiHostConnectionEnabled,
    isSearchProxyV3ConnectionEnabled: options?.isSearchProxyV3ConnectionEnabled,
  })

  const isReverseProxyEnabled =
    process.env.NEXT_PUBLIC_ALGOLIA_USE_REVERSE_PROXY === "true" && !canUseDirectAlgoliaConnection

  const searchClientOptions: AlgoliaSearchOptions = {
    authMode: AuthMode.WithinHeaders,
    timeouts: {
      // Timeout is set to 5s (default was 2s) to try to avoid "connect ETIMEDOUT" errors because search-proxy had no time to respond when switching to another Algolia instance
      connect: 5,
      read: 5,
      write: 30,
    },
    ...(isReverseProxyEnabled && searchProxyHosts && { hosts: searchProxyHosts }),
    ...(userToken && { headers: { "X-Algolia-UserToken": userToken } }),
  }

  logger.info(
    `Algolia "algoliasearch" client was initialized with params: ${JSON.stringify({
      apiKey: hideAndReplaceString(algoliaApiKey, "*", -3),
      appID: process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID,
      options: searchClientOptions,
    })}`
  )

  return algoliasearch(process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID, algoliaApiKey, searchClientOptions)
}

export { getSearchEngine }
